import { useCallback, useEffect, useRef } from "react";

/**
 * Avisa al salir de la página si hay cambios sin guardar.
 *
 * Cambiar de tenant en el header recarga la pantalla entera (es la forma en que
 * el BO refiltra todo), así que un formulario a medio cargar se pierde. El caso
 * de "no hay tenant elegido" lo cubre TenantGateLock deshabilitando el
 * formulario, que es prevención y no aviso. Esto cubre el otro caso: ya había un
 * tenant elegido, se cargó medio formulario y recién ahí se cambia de tenant.
 *
 * Se usa el `beforeunload` nativo y no un diálogo propio: la recarga la dispara
 * el selector del header, que vive fuera del árbol del formulario, y el aviso
 * del navegador cubre además cerrar la pestaña, el botón de atrás y F5.
 */
export const useUnsavedChangesGuard = (isDirty: boolean) => {
  const removeGuardRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!isDirty) return;

    const advertir = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Los navegadores modernos ignoran el texto y muestran el suyo, pero
      // sigue haciendo falta setearlo para que aparezca el diálogo.
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", advertir);
    const removeGuard = () => window.removeEventListener("beforeunload", advertir);
    removeGuardRef.current = removeGuard;
    return removeGuard;
  }, [isDirty]);

  return useCallback(() => removeGuardRef.current(), []);
};
